import React, { useState } from 'react';
import '../styles/ResultDisplay.css';

const ResultDisplay = ({ result, onClear }) => {
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const calculateCompressionRatio = () => {
    if (result.operation === 'compress' && result.originalText) {
      const originalSize = result.originalText.length;
      let compressedSize;
      
      if (result.algorithm === 'HUFFMAN') {
        compressedSize = result.bitString ? result.bitString.length / 8 : 0; // bits to bytes
      } else if (result.algorithm === 'LZW') {
        compressedSize = result.codes ? result.codes.length * 2 : 0; // assume 2 bytes per code
      }
      
      const ratio = originalSize > 0 ? (compressedSize / originalSize) * 100 : 0;
      const savings = Math.max(0, 100 - ratio);
      
      return {
        originalSize,
        compressedSize: Math.round(compressedSize),
        ratio: ratio.toFixed(2),
        savings: savings.toFixed(2)
      };
    }
    return null;
  };

  const stats = calculateCompressionRatio();

  return (
    <div className="result-display">
      <div className="result-header">
        <h3>
          <i className={`fas ${result.operation === 'compress' ? 'fa-compress' : 'fa-expand'}`}></i>
          {result.operation === 'compress' ? 'Compression' : 'Decompression'} Results
          <span className="algorithm-tag">{result.algorithm}</span>
        </h3>
        <button className="close-btn" onClick={onClear}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="result-content">
        {/* Compression Statistics */}
        {stats && (
          <div className="stats-section">
            <h4><i className="fas fa-chart-bar"></i> Compression Statistics</h4>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Original Size:</span>
                <span className="stat-value">{stats.originalSize} characters</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Compressed Size:</span>
                <span className="stat-value">{stats.compressedSize} bytes</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Compression Ratio:</span>
                <span className="stat-value">{stats.ratio}%</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Space Savings:</span>
                <span className="stat-value success">{stats.savings}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Results based on operation and algorithm */}
        {result.operation === 'compress' ? (
          <div className="results-section">
            {result.algorithm === 'HUFFMAN' && (
              <>
                <div className="result-item">
                  <div className="result-label">
                    <i className="fas fa-binary"></i>
                    Compressed Bit String:
                    <button 
                      className="copy-btn"
                      onClick={() => copyToClipboard(result.bitString, 'bitString')}
                    >
                      <i className={`fas ${copiedField === 'bitString' ? 'fa-check' : 'fa-copy'}`}></i>
                    </button>
                  </div>
                  <div className="result-value bit-string">
                    {result.bitString}
                  </div>
                  <div className="result-info">
                    Length: {result.bitString ? result.bitString.length : 0} bits
                  </div>
                </div>

                <div className="result-item">
                  <div className="result-label">
                    <i className="fas fa-table"></i>
                    Frequency Map:
                    <button 
                      className="copy-btn"
                      onClick={() => copyToClipboard(JSON.stringify(result.freqMap), 'freqMap')}
                    >
                      <i className={`fas ${copiedField === 'freqMap' ? 'fa-check' : 'fa-copy'}`}></i>
                    </button>
                  </div>
                  <div className="result-value json-data">
                    <pre>{JSON.stringify(result.freqMap, null, 2)}</pre>
                  </div>
                </div>
              </>
            )}

            {result.algorithm === 'LZW' && (
              <div className="result-item">
                <div className="result-label">
                  <i className="fas fa-list-ol"></i>
                  LZW Codes:
                  <button 
                    className="copy-btn"
                    onClick={() => copyToClipboard(JSON.stringify(result.codes), 'codes')}
                  >
                    <i className={`fas ${copiedField === 'codes' ? 'fa-check' : 'fa-copy'}`}></i>
                  </button>
                </div>
                <div className="result-value json-data">
                  <pre>{JSON.stringify(result.codes, null, 2)}</pre>
                </div>
                <div className="result-info">
                  Total codes: {result.codes ? result.codes.length : 0}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Decompression result
          <div className="results-section">
            <div className="result-item">
              <div className="result-label">
                <i className="fas fa-file-text"></i>
                Decompressed Text:
                <button 
                  className="copy-btn"
                  onClick={() => copyToClipboard(result.text, 'decompressed')}
                >
                  <i className={`fas ${copiedField === 'decompressed' ? 'fa-check' : 'fa-copy'}`}></i>
                </button>
              </div>
              <div className="result-value decompressed-text">
                {result.text}
              </div>
              <div className="result-info">
                Length: {result.text ? result.text.length : 0} characters
              </div>
            </div>
          </div>
        )}

        {/* Instructions for next steps */}
        {result.operation === 'compress' && (
          <div className="instructions-section">
            <h4><i className="fas fa-info-circle"></i> Next Steps</h4>
            <p>
              To decompress this data, switch to "Decompress" mode and paste the above 
              {result.algorithm === 'HUFFMAN' ? ' bit string and frequency map' : ' codes'} 
              into the corresponding fields.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultDisplay;
